import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { K8sResourceDescriptorDto } from '@mogenius/client-sdk/core-dto';
import { ClusterService } from '@mogenius/cluster/services/cluster-service/cluster.service';
import { KubernetesResourceService } from '@mogenius/kubernetes-resource/services/kubernetes-resource.service';
import { OrganizationPermissionService } from '@mogenius/organization/services/organization-permission/organization-permission.service';
import { NavigationService } from '@mogenius/shared/services/navigation/navigation.service';
import { NotificationService } from '@mogenius/shared/services/notification.service';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import {
  canUseCommandPalette,
  Command,
  CommandPaletteService,
  resolveResourceScope,
  resourceScopeRoute
} from './command-palette.service';
import { KeyboardShortcutService } from './keyboard-shortcut.service';
import { UserService } from '@mogenius/user/services/user/user.service';

type RouteContext = 'cluster' | 'workspace' | 'resource' | 'none';

@Injectable({ providedIn: 'root' })
export class AppCommandsService {
  private readonly _commandPalette = inject(CommandPaletteService);
  private readonly _keyboardShortcuts = inject(KeyboardShortcutService);
  private readonly _navigationService = inject(NavigationService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _k8sService = inject(KubernetesResourceService);
  private readonly _clusterService = inject(ClusterService);
  private readonly _userService = inject(UserService);
  private readonly _permissionService = inject(OrganizationPermissionService);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);

  private _toggleThemeFn: (() => void) | null = null;

  public init(toggleTheme: () => void): void {
    this._toggleThemeFn = toggleTheme;
    this.registerCommands();
    this.registerKeyboardShortcuts();
    this.setupContextualCommands();
  }

  public prefetchResourceDescriptors(): void {
    if (this._k8sService.resourceDescriptorList$.value?.length) return;
    if (!this._clusterService.selectedCluster$.value?.id) return;
    this._k8sService.resourceDescriptorList().pipe(take(1)).subscribe();
  }

  private isPaletteAvailable(): boolean {
    return canUseCommandPalette(this._navigationService.selectedOrganization$?.value, this._userService.user$?.value);
  }

  private navigateToResourceType(kind: string): void {
    const cached = this._k8sService.resourceDescriptorList$.value?.find((d) => d.kind === kind);
    if (cached) {
      this.performResourceTypeNav(kind, cached);
      return;
    }

    this._k8sService
      .resourceDescriptorList()
      .pipe(take(1))
      .subscribe({
        next: (descriptors) => {
          const descriptor = descriptors?.find((d) => d.kind === kind);
          this.performResourceTypeNav(kind, descriptor);
        },
        error: () => this.performResourceTypeNav(kind, undefined)
      });
  }

  private performResourceTypeNav(kind: string, descriptor?: K8sResourceDescriptorDto): void {
    const orgId = this._navigationService.selectedOrganization$.value?.id;
    const scope = this._commandPalette.resourceScope();
    if (!orgId || !scope) return;

    const queryParams: Record<string, string> = descriptor
      ? {
          sm_id: `cmdk-${kind.toLowerCase()}`,
          sm_name: kind,
          sm_resourceDescriptor: JSON.stringify(descriptor)
        }
      : { sm_name: kind };

    // This used to open the cluster's resources page from anywhere, so "Pods" inside a
    // workspace sent a workspace user into clusterPermissionResolver and out again.
    this._router.navigate(resourceScopeRoute(orgId, scope), { queryParams });
  }

  private registerCommands(): void {
    const nav = this._navigationService;

    const commands: Command[] = [
      // Navigation — k9s-style :command shortcuts
      {
        id: 'nav:dashboard',
        label: 'Dashboard',
        category: 'navigation',
        keywords: [':dashboard', ':dash', 'home', 'overview', 'start'],
        icon: 'pi pi-objects-column',
        shortcut: ':dash',
        execute: () => nav.navigate(['/', 'studio', 'organization', '##ORGANIZATION_ID##', 'dashboard'])
      },
      {
        id: 'nav:clusters',
        label: 'Clusters',
        category: 'navigation',
        keywords: [':clusters', ':cluster', ':ctx', 'kubernetes', 'k8s', 'infrastructure'],
        icon: 'pi pi-sitemap',
        shortcut: ':clusters',
        execute: () => nav.navigate(['/', 'studio', 'organization', '##ORGANIZATION_ID##', 'cluster'])
      },
      {
        id: 'nav:workspaces',
        label: 'Workspaces',
        category: 'navigation',
        keywords: [':workspaces', ':ws', ':namespaces', ':ns', 'namespace', 'project', 'environment'],
        icon: 'pi pi-th-large',
        shortcut: ':ws',
        execute: () => nav.navigate(['/', 'studio', 'organization', '##ORGANIZATION_ID##', 'workspaces'])
      },
      {
        id: 'nav:members',
        label: 'Members',
        category: 'navigation',
        keywords: [':members', ':users', 'team', 'people', 'permissions'],
        icon: 'pi pi-users',
        shortcut: ':members',
        execute: () => nav.navigate(['/', 'studio', 'organization', '##ORGANIZATION_ID##', 'members'])
      },
      {
        id: 'nav:settings',
        label: 'Settings',
        category: 'navigation',
        keywords: [':settings', ':config', 'preferences', 'configuration'],
        icon: 'pi pi-cog',
        shortcut: ':settings',
        execute: () => nav.navigate(['/', 'studio', 'organization', '##ORGANIZATION_ID##', 'settings'])
      },
      {
        id: 'nav:organizations',
        label: 'Organizations',
        category: 'navigation',
        keywords: [':organizations', ':orgs', ':org', 'switch', 'select'],
        icon: 'pi pi-building',
        shortcut: ':orgs',
        execute: () => nav.navigate(['/', 'studio', 'settings', 'organizations'])
      },
      {
        id: 'nav:profile',
        label: 'Profile',
        category: 'navigation',
        keywords: [':profile', ':user', ':me', 'account', 'personal'],
        icon: 'pi pi-user',
        shortcut: ':profile',
        execute: () => nav.navigate(['/', 'studio', 'settings', 'user'])
      },

      // Resource type views — k9s-style :resource navigation, into the current ResourceScope
      {
        id: 'nav:deployments',
        label: 'Deployments',
        category: 'navigation',
        keywords: [':deployments', ':deploy', ':dp', 'apps', 'workload'],
        icon: 'pi pi-send',
        shortcut: ':deploy',
        requiresResourceScope: true,
        execute: () => this.navigateToResourceType('Deployment')
      },
      {
        id: 'nav:pods',
        label: 'Pods',
        category: 'navigation',
        keywords: [':pods', ':pod', ':po', 'container', 'running'],
        icon: 'pi pi-microchip',
        shortcut: ':pods',
        requiresResourceScope: true,
        execute: () => this.navigateToResourceType('Pod')
      },
      {
        id: 'nav:services',
        label: 'Services',
        category: 'navigation',
        keywords: [':services', ':service', ':svc', 'networking', 'endpoint'],
        icon: 'pi pi-server',
        shortcut: ':svc',
        requiresResourceScope: true,
        execute: () => this.navigateToResourceType('Service')
      },
      {
        id: 'nav:configmaps',
        label: 'ConfigMaps',
        category: 'navigation',
        keywords: [':configmaps', ':configmap', ':cm', 'config', 'configuration'],
        icon: 'pi pi-file',
        shortcut: ':cm',
        requiresResourceScope: true,
        execute: () => this.navigateToResourceType('ConfigMap')
      },
      {
        id: 'nav:secrets',
        label: 'Secrets',
        category: 'navigation',
        keywords: [':secrets', ':secret', ':sec', 'credentials', 'tls'],
        icon: 'pi pi-key',
        shortcut: ':sec',
        requiresResourceScope: true,
        execute: () => this.navigateToResourceType('Secret')
      },
      {
        id: 'nav:ingresses',
        label: 'Ingresses',
        category: 'navigation',
        keywords: [':ingresses', ':ingress', ':ing', 'routing', 'domain', 'host'],
        icon: 'pi pi-globe',
        shortcut: ':ing',
        requiresResourceScope: true,
        execute: () => this.navigateToResourceType('Ingress')
      },
      {
        id: 'nav:cronjobs',
        label: 'CronJobs',
        category: 'navigation',
        keywords: [':cronjobs', ':cronjob', ':cj', 'schedule', 'cron', 'timer'],
        icon: 'pi pi-clock',
        shortcut: ':cj',
        requiresResourceScope: true,
        execute: () => this.navigateToResourceType('CronJob')
      },
      {
        id: 'nav:statefulsets',
        label: 'StatefulSets',
        category: 'navigation',
        keywords: [':statefulsets', ':statefulset', ':sts', 'stateful', 'database'],
        icon: 'pi pi-database',
        shortcut: ':sts',
        requiresResourceScope: true,
        execute: () => this.navigateToResourceType('StatefulSet')
      },
      {
        id: 'nav:daemonsets',
        label: 'DaemonSets',
        category: 'navigation',
        keywords: [':daemonsets', ':daemonset', ':ds', 'daemon', 'node'],
        icon: 'pi pi-sitemap',
        shortcut: ':ds',
        requiresResourceScope: true,
        execute: () => this.navigateToResourceType('DaemonSet')
      },

      // Actions
      {
        id: 'action:toggle-theme',
        label: 'Toggle Dark / Light Mode',
        category: 'action',
        keywords: [':theme', 'dark', 'light', 'mode', 'appearance'],
        icon: 'pi pi-moon',
        execute: () => this._toggleThemeFn?.()
      },
      {
        id: 'action:copy-url',
        label: 'Copy Current URL',
        category: 'action',
        keywords: [':copy', 'link', 'share', 'clipboard'],
        icon: 'pi pi-copy',
        execute: () => {
          navigator.clipboard.writeText(window.location.href).then(
            () => this._notificationService.success('URL copied to clipboard'),
            () => this._notificationService.warning('Could not copy — please use Cmd+C / Ctrl+C')
          );
        }
      }
    ];

    this._commandPalette.register(...commands);
  }

  private registerKeyboardShortcuts(): void {
    const cmdPalette = this._commandPalette;
    const canOpen = () => !cmdPalette.isOpen() && this.isPaletteAvailable();

    // k9s-style: ':' opens command palette with ':' pre-filled
    this._keyboardShortcuts.register({
      id: 'shortcut:colon-command',
      keys: ':',
      action: () => {
        this.prefetchResourceDescriptors();
        cmdPalette.open();
        cmdPalette.setQuery(':');
      },
      when: canOpen
    });

    this._keyboardShortcuts.register({
      id: 'shortcut:help',
      keys: '?',
      action: () => {
        this.prefetchResourceDescriptors();
        cmdPalette.open();
      },
      when: canOpen
    });
  }

  private setupContextualCommands(): void {
    // Watch for route changes and update contextual commands
    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map((event) => this.detectContext(event.urlAfterRedirects)),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((context) => {
        this.updateContextualCommands(context);
      });

    // Also watch for cluster/workspace selection changes
    combineLatest([
      this._clusterService.selectedCluster$,
      this._navigationService.selectedWorkspace$,
      this._permissionService.organizationPermission$
    ])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([cluster, workspace, permission]) => {
        // Update context for command filtering
        this._commandPalette.setContext(!!cluster?.id, !!workspace?.name);
        this._commandPalette.setResourceScope(
          resolveResourceScope(
            cluster ? { id: cluster.id, name: cluster.displayName } : null,
            workspace?.name,
            !!permission?.canChange
          )
        );
        // Re-evaluate context when selection changes
        const context = this.detectContext(this._router.url);
        this.updateContextualCommands(context);
      });
  }

  private detectContext(url: string): RouteContext {
    if (url.includes('/resources') || url.includes('/workload')) {
      return 'resource';
    }
    if (url.includes('/workspace/') || url.includes('/workspaces/')) {
      return 'workspace';
    }
    if (url.includes('/cluster/') && !url.includes('/cluster/list')) {
      return 'cluster';
    }
    return 'none';
  }

  private updateContextualCommands(context: RouteContext): void {
    // Remove old contextual commands
    this._commandPalette.unregister('ctx:create-secret', 'ctx:create-configmap');
    const nav = this._navigationService;

    // Register new contextual commands based on context
    if (context === 'cluster' || context === 'workspace' || context === 'resource') {
      const clusterCommands: Command[] = [
        {
          id: 'ctx:create-secret',
          label: 'Create Secret',
          category: 'action',
          keywords: [':create-secret', 'new secret', 'add secret'],
          icon: 'pi pi-plus',
          requiresWorkspace: true,
          execute: () =>
            nav.navigate([
              '/',
              'studio',
              'organization',
              '##ORGANIZATION_ID##',
              'cluster',
              '##CLUSTER_ID##',
              'workspaces',
              '##WORKSPACE_NAME##',
              'resources',
              'namespace',
              'create-resource',
              'Secret'
            ])
        },
        {
          id: 'ctx:create-configmap',
          label: 'Create ConfigMap',
          category: 'action',
          keywords: [':create-configmap', ':create-cm', 'new configmap', 'add config'],
          icon: 'pi pi-plus',
          requiresWorkspace: true,
          execute: () =>
            nav.navigate([
              '/',
              'studio',
              'organization',
              '##ORGANIZATION_ID##',
              'cluster',
              '##CLUSTER_ID##',
              'workspaces',
              '##WORKSPACE_NAME##',
              'resources',
              'namespace',
              'create-resource',
              'ConfigMap'
            ])
        }
      ];

      // Only show create commands if we have a workspace selected
      if (this._navigationService.selectedWorkspace$.value?.name) {
        this._commandPalette.register(...clusterCommands);
      }
    }

    // Resource-specific actions (logs, shell, yaml, describe) removed for now
    // They need to be bound to a selected resource to work properly
  }
}
